// src/pages/Dashboard.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import axios from "../axiosConfig";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Award,
  BadgeDollarSign,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Gift,
  MapPinned,
  PackageCheck,
  Sparkles,
  Ticket,
  Users,
} from "lucide-react";

import ModalPreencherCoordenadas from "../components/ModalPreencherCoordenadas";
import "./Dashboard.css";

function Dashboard() {
  const [resumos, setResumos] = useState({
    campanhas: 0,
    clientes: 0,
    operacoes: 0,
  });

  const [graficoOperacoes, setGraficoOperacoes] = useState([]);
  const [graficoPontos, setGraficoPontos] = useState({
    ganhos: 0,
    expirados: 0,
    usados: 0,
  });

  const [valorOperacoesMes, setValorOperacoesMes] = useState(0);
  const [rankingClientes, setRankingClientes] = useState([]);
  const [ultimasCampanhas, setUltimasCampanhas] = useState([]);
  const [usuario, setUsuario] = useState(null);
  const [fallbackHandled, setFallbackHandled] = useState(false);

  const [resgatesAprovacao, setResgatesAprovacao] = useState(0);
  const [operacoesAprovacao, setOperacoesAprovacao] = useState(0);
  const [resgatesExpedicao, setResgatesExpedicao] = useState(0);
  const [pixPendentes, setPixPendentes] = useState(0);
  const [pixContasAPagar, setPixContasAPagar] = useState(0);

  const [carregandoCoordenadas, setCarregandoCoordenadas] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);

  const operacoesRef = useRef([]);
  const navigate = useNavigate();

  const dashboardColors = ["#1b4fd8", "#1bb3cd", "#65d0bf", "#ff7c00", "#ef4444"];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("usuario"));
    const token = localStorage.getItem("token");

    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    }

    setUsuario(user);
  }, []);

  useEffect(() => {
    if (!usuario) return;

    const podeVerDashboard = usuario?.permissoes?.some(
      (p) => p.rota === "dashboard" && p.pode_ler
    );

    if (!podeVerDashboard) {
      const rotaLiberada = usuario?.permissoes?.find(
        (p) => p.pode_ler && p.rota !== "dashboard"
      );

      if (rotaLiberada) {
        setFallbackHandled(true);
        setTimeout(() => navigate(`/${rotaLiberada.rota}`), 800);
        return;
      }

      setFallbackHandled("no-permission");
      return;
    }

    carregarCampanhas();
    carregarClientes();
    carregarOperacoesUnificadas();
    carregarGraficoPontos();
    carregarPixResumo();

    setTimeout(() => {
      if (usuario?.tipo !== "cliente") {
        carregarRankingClientes();
      }
    }, 400);
  }, [usuario, navigate]);

  const carregarPixResumo = async () => {
    try {
      const podeVerPix =
        usuario?.tipo === "admin" ||
        usuario?.permissoes?.some((p) => p.rota === "pix" && p.pode_ler);

      if (!podeVerPix) return;

      const res = await axios.get("/dashboard/pix-counters");
      setPixPendentes(Number(res.data?.pendentes || 0));
      setPixContasAPagar(Number(res.data?.contas_a_pagar || 0));
    } catch (err) {
      console.error("Erro ao carregar resumo Pix:", err);
    }
  };

  const carregarCampanhas = async () => {
    try {
      const res = await axios.get("/campanhas");

      setResumos((prev) => ({
        ...prev,
        campanhas: Array.isArray(res.data) ? res.data.length : 0,
      }));

      const ordenadas = [...(res.data || [])].sort(
        (a, b) => new Date(b.data_inicio) - new Date(a.data_inicio)
      );

      setUltimasCampanhas(ordenadas.slice(0, 3));
    } catch (err) {
      console.error("Erro ao carregar campanhas:", err);
    }
  };

  const carregarClientes = async () => {
    try {
      const res = await axios.get("/clientes");

      setResumos((prev) => ({
        ...prev,
        clientes: Array.isArray(res.data) ? res.data.length : 0,
      }));
    } catch (err) {
      console.error("Erro ao carregar clientes:", err);
    }
  };

  const carregarOperacoesUnificadas = async () => {
    try {
      const res = await axios.get("/operacoes");
      const operacoes = Array.isArray(res.data) ? res.data : [];

      operacoesRef.current = operacoes;

      setResumos((prev) => ({
        ...prev,
        operacoes: operacoes.length,
      }));

      const contagem = operacoes.reduce((acc, op) => {
        const status = op.status || "indefinido";
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      setGraficoOperacoes(
        Object.entries(contagem).map(([status, total]) => ({
          status,
          total,
        }))
      );

      const hoje = new Date();
      const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

      const operacoesMes = operacoes.filter((op) => {
        const data = new Date(op.data_operacao);
        return data >= inicio && data <= fim;
      });

      const totalMes = operacoesMes.reduce(
        (acc, op) => acc + parseFloat(op.valor || 0),
        0
      );

      setValorOperacoesMes(totalMes);
      setOperacoesAprovacao(
        operacoes.filter((op) => op.status === "pendente").length
      );

      carregarAvisosExpedicao();
    } catch (err) {
      console.error("Erro ao carregar operações:", err);
    }
  };

  const carregarAvisosExpedicao = async () => {
    try {
      const resExpedicao = await axios.get("/resgates-expedicao");
      const expedicao = Array.isArray(resExpedicao.data) ? resExpedicao.data : [];

      setResgatesExpedicao(
        expedicao.filter(
          (r) => r.status === "preparacao" || r.status === "transporte"
        ).length
      );

      const resResgates = await axios.get("/resgates");
      const resgates = Array.isArray(resResgates.data) ? resResgates.data : [];

      setResgatesAprovacao(resgates.filter((r) => r.status === "pedido").length);
    } catch (err) {
      console.error("Erro ao carregar avisos:", err);
    }
  };

  const carregarGraficoPontos = async () => {
    try {
      const hoje = new Date();

      const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
        .toISOString()
        .slice(0, 10);

      const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)
        .toISOString()
        .slice(0, 10);

      const res = await axios.get(
        `/relatorio-pontos?dataInicio=${inicio}&dataFim=${fim}`
      );

      setGraficoPontos(res.data || { ganhos: 0, expirados: 0, usados: 0 });
    } catch (err) {
      console.error("Erro ao carregar gráfico de pontos:", err);
    }
  };

  const carregarRankingClientes = async () => {
    try {
      const res = await axios.get("/dashboard/ranking-clientes");
      setRankingClientes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erro ao carregar ranking de clientes:", err);
    }
  };

  const handlePreencherCoordenadas = async () => {
    toast.info("Iniciando preenchimento de coordenadas...");
    setCarregandoCoordenadas(true);

    try {
      const res = await axios.post(
        "/admin/preencher-coordenadas-empresas",
        {},
        {
          timeout: 120000,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      toast.success(`Coordenadas preenchidas: ${res.data.novos} novos.`);
    } catch (err) {
      console.error("Erro ao preencher coordenadas:", err);
      toast.error("Erro ao preencher coordenadas.");
    } finally {
      setCarregandoCoordenadas(false);
    }
  };

  const podePreencherCoordenadas =
    usuario?.tipo === "admin" &&
    usuario?.permissoes?.some(
      (p) => p.rota === "empresas" && p.pode_editar
    );

  const pontosChartData = useMemo(
    () => [
      {
        name: "Ganhos",
        valor: Number(graficoPontos.ganhos || 0),
      },
      {
        name: "Usados",
        valor: Number(graficoPontos.usados || 0),
      },
      {
        name: "Expirados",
        valor: Number(graficoPontos.expirados || 0),
      },
    ],
    [graficoPontos]
  );

  const operacoesAreaData = useMemo(() => {
    const aprovadas = graficoOperacoes.find((g) => g.status === "aprovado")?.total || 0;
    const pendentes = graficoOperacoes.find((g) => g.status === "pendente")?.total || 0;
    const rejeitadas = graficoOperacoes.find((g) => g.status === "rejeitado")?.total || 0;

    return [
      { name: "Aprovadas", total: aprovadas },
      { name: "Pendentes", total: pendentes },
      { name: "Rejeitadas", total: rejeitadas },
    ];
  }, [graficoOperacoes]);

  const formatarMoeda = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });

  const formatarNumero = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    });

  const formatarData = (data) => {
    if (!data) return "—";

    try {
      return new Date(data).toLocaleDateString("pt-BR");
    } catch {
      return "—";
    }
  };

  if (fallbackHandled === true) {
    return (
      <div className="dashboardAdmin-state">
        <Sparkles size={22} />
        <p>Redirecionando para sua área de acesso...</p>
      </div>
    );
  }

  if (fallbackHandled === "no-permission") {
    return (
      <div className="dashboardAdmin-state">
        <AlertCircle size={26} />
        <h2>Olá, {usuario?.nome}!</h2>
        <p>
          Sua conta ainda não tem permissões liberadas para acessar nenhuma seção.
          Entre em contato com um administrador.
        </p>
      </div>
    );
  }

  if (!usuario) return null;

  return (
    <div className="dashboardAdmin-page">
      <div className="dashboardAdmin-container">
        <section className="dashboardAdmin-hero">
          <div>
            <span className="dashboardAdmin-kicker">Visão geral</span>
            <h1>Dashboard Administrativo</h1>
            <p>
              Acompanhe clientes, operações, pontos, resgates e pendências do
              programa Tikpontos em uma visão consolidada.
            </p>
          </div>

          {podePreencherCoordenadas && (
            <button
              type="button"
              className="dashboardAdmin-heroBtn"
              onClick={() => setModalAberto(true)}
              disabled={carregandoCoordenadas}
            >
              <MapPinned size={16} />
              {carregandoCoordenadas ? "Processando..." : "Preencher coordenadas"}
            </button>
          )}
        </section>

        <section className="dashboardAdmin-kpis">
          <article className="dashboardAdmin-kpi">
            <div className="dashboardAdmin-kpiIcon blue">
              <Ticket size={20} />
            </div>
            <span>Total de campanhas</span>
            <strong>{formatarNumero(resumos.campanhas)}</strong>
          </article>

          <article className="dashboardAdmin-kpi">
            <div className="dashboardAdmin-kpiIcon cyan">
              <Users size={20} />
            </div>
            <span>Total de clientes</span>
            <strong>{formatarNumero(resumos.clientes)}</strong>
          </article>

          <article className="dashboardAdmin-kpi">
            <div className="dashboardAdmin-kpiIcon green">
              <ClipboardList size={20} />
            </div>
            <span>Total de operações</span>
            <strong>{formatarNumero(resumos.operacoes)}</strong>
          </article>

          <article className="dashboardAdmin-kpi featured">
            <div className="dashboardAdmin-kpiIcon orange">
              <BadgeDollarSign size={20} />
            </div>
            <span>Operações no mês</span>
            <strong>{formatarMoeda(valorOperacoesMes)}</strong>
          </article>
        </section>

        <section className="dashboardAdmin-alertGrid">
          <button
            type="button"
            className="dashboardAdmin-alert purple"
            onClick={() => navigate("/expedicao")}
          >
            <PackageCheck size={18} />
            <span>
              <strong>{resgatesExpedicao}</strong> pedido(s) em expedição
            </span>
            <small>Visualizar</small>
          </button>

          {usuario?.tipo !== "cliente" && (
            <>
              <button
                type="button"
                className="dashboardAdmin-alert orange"
                onClick={() => navigate("/admin/pix")}
              >
                <BadgeDollarSign size={18} />
                <span>
                  <strong>{pixPendentes}</strong> Pix pendente(s)
                </span>
                <small>Aprovar</small>
              </button>

              <button
                type="button"
                className="dashboardAdmin-alert cyan"
                onClick={() => navigate("/admin/pix/contas-a-pagar")}
              >
                <BadgeDollarSign size={18} />
                <span>
                  <strong>{pixContasAPagar}</strong> conta(s) Pix a pagar
                </span>
                <small>Visualizar</small>
              </button>
            </>
          )}

          <button
            type="button"
            className="dashboardAdmin-alert green"
            onClick={() => navigate("/resgates")}
          >
            <Gift size={18} />
            <span>
              <strong>{resgatesAprovacao}</strong> resgate(s) aguardando aprovação
            </span>
            <small>Visualizar</small>
          </button>

          <button
            type="button"
            className="dashboardAdmin-alert blue"
            onClick={() => navigate("/operacoes")}
          >
            <CheckCircle2 size={18} />
            <span>
              <strong>{operacoesAprovacao}</strong> operação(ões) pendente(s)
            </span>
            <small>Visualizar</small>
          </button>
        </section>

        <section className="dashboardAdmin-mainGrid">
          <article className="dashboardAdmin-panel">
            <div className="dashboardAdmin-panelHeader">
              <div>
                <span>Campanhas</span>
                <h3>Últimas campanhas</h3>
              </div>
              <CalendarDays size={18} />
            </div>

            <div className="dashboardAdmin-tableWrap">
              <table className="dashboardAdmin-table">
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>Início</th>
                    <th>Fim</th>
                  </tr>
                </thead>

                <tbody>
                  {ultimasCampanhas.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="dashboardAdmin-empty">
                        Nenhuma campanha encontrada.
                      </td>
                    </tr>
                  ) : (
                    ultimasCampanhas.map((campanha) => (
                      <tr key={campanha.id}>
                        <td>{campanha.titulo}</td>
                        <td>{formatarData(campanha.data_inicio)}</td>
                        <td>{formatarData(campanha.data_fim)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </article>

          <article className="dashboardAdmin-panel">
            <div className="dashboardAdmin-panelHeader">
              <div>
                <span>Operações</span>
                <h3>Status das operações</h3>
              </div>
              <BarChart3 size={18} />
            </div>

            {graficoOperacoes.length === 0 ? (
              <div className="dashboardAdmin-emptyChart">Carregando gráfico...</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={graficoOperacoes}
                    dataKey="total"
                    nameKey="status"
                    innerRadius={58}
                    outerRadius={92}
                    paddingAngle={4}
                  >
                    {graficoOperacoes.map((_, index) => (
                      <Cell
                        key={`status-${index}`}
                        fill={dashboardColors[index % dashboardColors.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </article>

          <article className="dashboardAdmin-panel">
            <div className="dashboardAdmin-panelHeader">
              <div>
                <span>Pontos</span>
                <h3>Pontos no mês atual</h3>
              </div>
              <Award size={18} />
            </div>

            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={pontosChartData} barSize={34}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="valor" radius={[8, 8, 0, 0]}>
                  {pontosChartData.map((_, index) => (
                    <Cell
                      key={`pontos-${index}`}
                      fill={dashboardColors[index % dashboardColors.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </article>
        </section>

        <section className="dashboardAdmin-wideGrid">
          <article className="dashboardAdmin-panel">
            <div className="dashboardAdmin-panelHeader">
              <div>
                <span>Resumo operacional</span>
                <h3>Distribuição de operações</h3>
              </div>
              <BarChart3 size={18} />
            </div>

            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={operacoesAreaData}>
                <defs>
                  <linearGradient id="dashboardArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1bb3cd" stopOpacity={0.55} />
                    <stop offset="95%" stopColor="#1bb3cd" stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="total"
                  stroke="#1b4fd8"
                  fill="url(#dashboardArea)"
                  strokeWidth={3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </article>

          {usuario?.tipo !== "cliente" && (
            <article className="dashboardAdmin-panel">
              <div className="dashboardAdmin-panelHeader">
                <div>
                  <span>Ranking</span>
                  <h3>Top 10 clientes com mais pontos</h3>
                </div>
                <Award size={18} />
              </div>

              <div className="dashboardAdmin-tableWrap">
                <table className="dashboardAdmin-table ranking">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Nome</th>
                      <th>Empresa</th>
                      <th>Cidade</th>
                      <th>UF</th>
                      <th>Pontos</th>
                    </tr>
                  </thead>

                  <tbody>
                    {rankingClientes.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="dashboardAdmin-empty">
                          Nenhum cliente encontrado.
                        </td>
                      </tr>
                    ) : (
                      rankingClientes.map((cliente, index) => (
                        <tr key={cliente.id}>
                          <td>
                            <span className="dashboardAdmin-rank">{index + 1}</span>
                          </td>
                          <td>{cliente.nome}</td>
                          <td>{cliente.empresa}</td>
                          <td>{cliente.cidade}</td>
                          <td>{cliente.uf}</td>
                          <td>
                            <strong>{formatarNumero(cliente.total_pontos)} pts</strong>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </article>
          )}
        </section>

        <ModalPreencherCoordenadas
          aberto={modalAberto}
          onClose={() => setModalAberto(false)}
          onConfirm={handlePreencherCoordenadas}
        />
      </div>
    </div>
  );
}

export default Dashboard;